import {
  Drawer,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  Button,
  Heading,
  Text,
  Flex,
  Spacer,
  useToast,
  DrawerBody,
  Skeleton,
} from '@chakra-ui/react';
import React, { useCallback, useMemo, useState } from 'react';

import {
  finleyAssert,
  utils as commonUtils,
} from '@finley-technologies/common';
import { FinleyAi as FinleyAiTypes } from '@finley-technologies/finley-types';

import {
  GetActiveCdqConfigsDocumentGQLType,
  useCreateCdqMutation,
} from './cdq.gql.generated';
import { CollateralDataQualityCheckFinleyAiChatContainer } from './CollateralDataQualityCheckFinleyAiChatContainer';
import { CollateralDataQualityCheckManualQueryEditContainer } from './CollateralDataQualityCheckManualQueryEditContainer';
import { AddCdqDrawerDisplayMode, cdqRawPromptTemplateString } from './utils';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import { useGetCreditFacilitiesByOrganizationQuery } from '~/contexts/CreditFacilitiesProvider/requests/getCreditFacilities.gql.generated';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { useGetAthenaTablesForOrganizationQuery } from '~/packages/athena/requests/requests.gql.generated';
import { filterAthenaTablesForCreditFacilities } from '~/packages/athena/ui/utils';
import { colors } from '~/packages/common-frontend/colors';
import { Icon } from '~/packages/common-frontend/components/icons';
import {
  errorToastConfig,
  successToastConfig,
} from '~/packages/common-frontend/constants';
import { useFinleyAiThreads } from '~/packages/finleyAi/hooks/useFinleyAiThreads';
import {
  drawerDisplayModeToHeaderNavigationLabel,
  FinleyAiDrawerDisplayMode,
} from '~/packages/finleyAi/ui/FinleyAiAthenaSqlGenerationDrawerContainer';
import { FinleyAiAthenaSqlGenerationDrawerHistoryView } from '~/packages/finleyAi/ui/FinleyAiAthenaSqlGenerationDrawerHistoryView';
import { FinleyAiAthenaSqlGenerationDrawerTablesView } from '~/packages/finleyAi/ui/FinleyAiAthenaSqlGenerationDrawerTablesView';
import { initFinleyAiViewContext } from '~/packages/finleyAi/ui/FinleyAiUtils';

const DrawerSubHeader = ({
  drawerDisplayMode,
  setDrawerDisplayMode,
}: {
  drawerDisplayMode: AddCdqDrawerDisplayMode;
  setDrawerDisplayMode: React.Dispatch<
    React.SetStateAction<AddCdqDrawerDisplayMode>
  >;
}) => {
  if (drawerDisplayMode === 'manual') {
    return (
      <>
        <Text color={colors.black[70]} fontSize="16px" fontWeight={400}>
          Write the SQL query to perform your collateral data quality check
          test. If you need assistance you can&nbsp;
          <Text
            as="span"
            color={colors.info}
            _hover={{ textDecoration: 'underline' }}
            onClick={() => setDrawerDisplayMode('tables')}
            cursor="pointer"
          >
            write your SQL query with FinleyAI.
          </Text>
        </Text>
      </>
    );
  }

  return (
    <>
      <Text color={colors.black[70]} fontSize="16px" fontWeight={400}>
        FinleyAI will assist you in writing the SQL query necessary to perform
        your collateral data quality check test. You can start by selecting the
        tables you&apos;d like to query, or you can&nbsp;
        <Text
          as="span"
          color={colors.info}
          _hover={{ textDecoration: 'underline' }}
          onClick={() => setDrawerDisplayMode('manual')}
          cursor="pointer"
        >
          write your own SQL query.
        </Text>
      </Text>
    </>
  );
};

type CollateralDataQualityCheckCdqDrawerContainerBaseProps = {
  creditFacilityId?: string; // pass in for creditFacility-scoped Cdq
  isOpen: boolean;
  onClose: () => void;
  initialQuery: string; // if passing in through edit mode
  mode: 'add' | 'edit';
};
interface CollateralDataQualityCheckAddCdqDrawerContainerProps
  extends CollateralDataQualityCheckCdqDrawerContainerBaseProps {
  mode: 'add';
}
interface CollateralDataQualityCheckEditCdqDrawerContainerProps
  extends CollateralDataQualityCheckCdqDrawerContainerBaseProps {
  mode: 'edit';
  selectedCdqConfigName: string;
}
type CollateralDataQualityCheckAddEditCdqDrawerContainerProps =
  | CollateralDataQualityCheckAddCdqDrawerContainerProps
  | CollateralDataQualityCheckEditCdqDrawerContainerProps;

export const CollateralDataQualityCheckAddEditCdqDrawerContainer = ({
  creditFacilityId,
  isOpen,
  onClose,
  initialQuery,
  mode,
}: CollateralDataQualityCheckAddEditCdqDrawerContainerProps) => {
  const toast = useToast();
  const { user } = useUserAuthOrThrow();
  const { organizationId } = user;

  const initialViewContext = initFinleyAiViewContext({
    type: 'athenaSqlGeneration',
    targetOrganizationId: user.organizationId,
    promptTemplate: cdqRawPromptTemplateString,
  });

  finleyAssert(
    initialViewContext.type === 'athenaSqlGeneration',
    'Only able to initialize Finley AI for portfolio covenants for SQL generation',
  );

  const [
    drawerDisplayMode,
    setDrawerDisplayMode,
  ] = useState<AddCdqDrawerDisplayMode>('tables');
  const [
    initialManualSqlQueryInput,
    setManualSqlInitialQuery,
  ] = useState<string>(initialQuery);

  const [tempPrompt, setTempPrompt] = useState<string>('');
  const [prompt, setPrompt] = useState<string>('');

  const finleyAiThreadsOutput = useFinleyAiThreads({
    prompt,
    setPrompt,
    tempPrompt,
    setTempPrompt,
    initialViewContext,
    promptTemplate: cdqRawPromptTemplateString,
  });

  const {
    onReset,
    viewContext,
    setViewContext,
    handleFinleyAiThreadSelection,
  } = finleyAiThreadsOutput;

  finleyAssert(
    viewContext.type === 'athenaSqlGeneration',
    'Sql Generation Finley AI can only be used for athenaSqlGeneration type',
  );

  const {
    data: creditFacilitiesData,
    loading: isCreditFacilitiesDataLoading,
  } = useGetCreditFacilitiesByOrganizationQuery({
    variables: {
      organizationId: user.organizationId,
      creditFacilitySlugsToHide: [],
    },
  });

  const {
    data: athenaTablesData,
    loading: isAthenaTablesDataLoading,
  } = useGetAthenaTablesForOrganizationQuery({
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: { organizationId: user.organizationId },
  });

  const [createCdq, { loading: isCreatingCdq }] = useCreateCdqMutation({
    refetchQueries: [GetActiveCdqConfigsDocumentGQLType],
  });

  const onCloseDrawer: () => void = useCallback(() => {
    onReset({
      finleyAiType: initialViewContext.type,
      targetOrgId: initialViewContext.targetOrganizationId,
    });
    setDrawerDisplayMode('tables');
    setManualSqlInitialQuery(initialQuery);
    onClose();
  }, [
    onReset,
    setDrawerDisplayMode,
    onClose,
    initialViewContext,
    initialQuery,
  ]);

  const onConfirmAddCdq = useCallback(
    async ({ name, query }: { name: string; query: string }) => {
      const response = await createCdq({
        variables: {
          name,
          decodedQuery: query,
          organizationId,
          creditFacilityId,
        },
      });

      if (response.data && !response.data.createCDQ.errors?.length) {
        toast({
          ...successToastConfig,
          title: `Successfully added ${name}`,
        });

        onCloseDrawer();
      } else if (
        response.errors?.length ||
        response.data?.createCDQ.errors?.length
      ) {
        const errorToastTitle = commonUtils.EnvUtils.isProduction()
          ? 'An unexpected error occurred. Please contact Finley for support.'
          : `${response.errors?.join(
              ', ',
            )}, ${response.data?.createCDQ.errors?.join(', ')}`;

        toast({ ...errorToastConfig, title: errorToastTitle });
      }
    },
    [createCdq, creditFacilityId, organizationId, toast, onCloseDrawer],
  );

  const onClickEditFinleyAiSql = useCallback((sqlQueryString: string) => {
    setManualSqlInitialQuery(sqlQueryString);
    setDrawerDisplayMode('manual');
  }, []);

  const drawerDisplayModeToHeaderNavigationAction: Record<
    FinleyAiDrawerDisplayMode,
    () => void
  > = useMemo(() => {
    const onViewHistory: () => void = () => {
      onReset({
        finleyAiType: initialViewContext.type,
        targetOrgId: initialViewContext.targetOrganizationId,
      });
      setDrawerDisplayMode('history');
    };

    const onNewChat: () => void = () => {
      onReset({
        finleyAiType: initialViewContext.type,
        targetOrgId: initialViewContext.targetOrganizationId,
      });
      setDrawerDisplayMode('tables');
    };

    return {
      tables: onViewHistory,
      chat: onViewHistory,
      history: onNewChat,
    };
  }, [onReset, setDrawerDisplayMode, initialViewContext]);

  const onSelectHistoricalThread = useCallback(
    (thread: FinleyAiTypes.FinleyAiThread) => {
      handleFinleyAiThreadSelection(thread);
      setDrawerDisplayMode('chat');
    },
    [handleFinleyAiThreadSelection],
  );

  if (
    !athenaTablesData ||
    isAthenaTablesDataLoading ||
    !creditFacilitiesData ||
    isCreditFacilitiesDataLoading
  ) {
    return (
      <Drawer
        isOpen={isOpen}
        placement="right"
        onClose={onCloseDrawer}
        size="xl"
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerHeader>
            <Flex alignItems="center" mb="24px">
              <Heading
                as="h2"
                fontSize="24px"
                lineHeight="32px"
                fontWeight={600}
              >
                {`${
                  mode === 'add' ? 'Add' : 'Edit'
                } Collateral Data Quality Check`}
              </Heading>
              <Spacer />
              <Button w="40px" variant="ghost" onClick={onCloseDrawer}>
                <Icon icon="x" />
              </Button>
            </Flex>
          </DrawerHeader>

          <DrawerBody>
            <Skeleton h="500px" />
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    );
  }

  const athenaTablesForCreditFacility = filterAthenaTablesForCreditFacilities({
    athenaTables: athenaTablesData.athenaTables,
    creditFacilitySlugs: creditFacilitiesData.organization.creditFacilities.map(
      (creditFacility) => creditFacility.slug,
    ),
  });

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onCloseDrawer} size="xl">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerHeader>
          <Flex alignItems="center" mb="24px">
            <Heading as="h2" fontSize="24px" lineHeight="32px" fontWeight={600}>
              {`${
                mode === 'add' ? 'Add' : 'Edit'
              } Collateral Data Quality Check`}
            </Heading>
            <Spacer />

            {drawerDisplayMode !== 'manual' && (
              <Button
                variant="ghost"
                color={colors.black[70]}
                fontWeight={400}
                onClick={
                  drawerDisplayModeToHeaderNavigationAction[drawerDisplayMode]
                }
              >
                {drawerDisplayModeToHeaderNavigationLabel[drawerDisplayMode]}
              </Button>
            )}

            <Button w="40px" variant="ghost" onClick={onCloseDrawer}>
              <Icon icon="x" />
            </Button>
          </Flex>
          <DrawerSubHeader
            drawerDisplayMode={drawerDisplayMode}
            setDrawerDisplayMode={setDrawerDisplayMode}
          />
        </DrawerHeader>

        {drawerDisplayMode === 'manual' && (
          <CollateralDataQualityCheckManualQueryEditContainer
            organizationId={organizationId}
            initialQuery={initialManualSqlQueryInput}
            athenaTables={athenaTablesForCreditFacility}
            onConfirmAddCdq={onConfirmAddCdq}
            isAddLoading={isCreatingCdq}
          />
        )}

        {drawerDisplayMode === 'tables' && (
          <FinleyAiAthenaSqlGenerationDrawerTablesView
            onClickNext={() => setDrawerDisplayMode('chat')}
            viewContext={viewContext}
            setViewContext={setViewContext}
            creditFacilityId={creditFacilityId}
          />
        )}

        {drawerDisplayMode === 'chat' && (
          <CollateralDataQualityCheckFinleyAiChatContainer
            organizationId={organizationId}
            initialQuery={initialManualSqlQueryInput}
            athenaTables={athenaTablesForCreditFacility}
            onConfirmAddCdq={onConfirmAddCdq}
            isAddLoading={isCreatingCdq}
            finleyAiThreadInputs={{
              prompt,
              setPrompt,
              tempPrompt,
              setTempPrompt,
              initialViewContext,
              promptTemplate: cdqRawPromptTemplateString,
            }}
            finleyAiThreadOutputs={finleyAiThreadsOutput}
            onClickEditFinleyAiSql={onClickEditFinleyAiSql}
          />
        )}

        {drawerDisplayMode === 'history' && (
          <FinleyAiAthenaSqlGenerationDrawerHistoryView
            userId={user.id}
            viewContext={viewContext}
            handleFinleyAiThreadSelection={onSelectHistoricalThread}
          />
        )}
      </DrawerContent>
    </Drawer>
  );
};
